import { ApiErrorHandler } from '../../../utils/apiErrorHandler';
import {
    FormDefinition,
    FormSubmissionRequest,
    FormSubmissionResponse,
    ValidationResult,
    FormDraft,
    FileUploadRequest,
    FileUploadResponse,
    FormApiError,
    OfferCampaignStatus,
    ActiveOffersResponse
} from '../types/form.types';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || '';
const FORMS_BASE_URL = `${BACKEND_API_URL}/forms`;

/**
 * Forms API Service
 * Handles all API calls to the Forms backend
 */
export class FormsApi {
    private static getRegionFromPathname(): string {
        const firstSegment = (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
        // Region routes are 2-letter segments like /gb, /us, /de.
        return /^[a-z]{2}$/.test(firstSegment) ? firstSegment : 'gb';
    }

    static async getActiveOffers(region: string = this.getRegionFromPathname()): Promise<ActiveOffersResponse> {
        return ApiErrorHandler.withRetry(
            async () => {
                const response = await fetch(
                    `${BACKEND_API_URL}/offers/active/?region=${encodeURIComponent(region)}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include'
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch active offers');
                }

                return (await response.json()) as ActiveOffersResponse;
            },
            { maxRetries: 2, baseDelay: 500 },
            `get_active_offers_${region}`
        );
    }

    private static getFieldValueByKey(
        fields: { [key: string]: string },
        targetKey: string
    ): string | null {
        const target = targetKey.toLowerCase();
        const matchedEntry = Object.entries(fields).find(([key]) => key.toLowerCase() === target);
        return matchedEntry ? matchedEntry[1] : null;
    }

    private static getRegionFromCredential(
        fields: { [key: string]: string }
    ): string | null {
        const credential = this.getFieldValueByKey(fields, 'x-amz-credential');
        if (!credential) return null;

        const parts = credential.split('/');
        // AWS4 credential format: accessKey/date/region/service/aws4_request
        return parts.length >= 3 ? parts[2] : null;
    }

    private static getXmlValue(xmlText: string, tagName: string): string | null {
        const pattern = new RegExp(`<${tagName}>([^<]+)</${tagName}>`);
        const match = xmlText.match(pattern);
        return match && match[1] ? match[1] : null;
    }

    private static buildRegionalS3UploadUrl(
        originalUploadUrl: string,
        region: string
    ): string | null {
        try {
            const parsedUrl = new URL(originalUploadUrl);
            const host = parsedUrl.hostname;

            // Virtual-hosted style: <bucket>.s3.amazonaws.com
            const hostMatch = host.match(/^(.+)\.s3\.amazonaws\.com$/);
            if (hostMatch && hostMatch[1]) {
                return `https://${hostMatch[1]}.s3.${region}.amazonaws.com/`;
            }

            // Path style fallback: s3.amazonaws.com/<bucket>
            if (host === 's3.amazonaws.com') {
                const bucket = parsedUrl.pathname.replace(/^\/+/, '').split('/')[0];
                if (bucket) {
                    return `https://${bucket}.s3.${region}.amazonaws.com/`;
                }
            }

            return null;
        } catch {
            return null;
        }
    }

    private static async postFileToS3(
        uploadUrl: string,
        fields: { [key: string]: string },
        file: File
    ): Promise<Response> {
        const formData = new FormData();

        // Add presigned fields first
        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
        });

        // Add file last
        formData.append('file', file);

        return fetch(uploadUrl, {
            method: 'POST',
            body: formData
        });
    }

    /**
     * Fetch form definition
     */
    static async getFormDefinition(formId: string): Promise<FormDefinition> {
        return ApiErrorHandler.withRetry(
            async () => {
                const response = await fetch(`${FORMS_BASE_URL}/${formId}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include' // Include session cookies
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Form not found');
                    }
                    throw new Error('Failed to load form definition');
                }

                return await response.json();
            },
            { maxRetries: 2, baseDelay: 500 },
            `get_form_definition_${formId}`
        );
    }

    /**
     * Validate step data
     */
    static async validateStep(
        formId: string,
        stepId: string,
        responses: { [key: string]: any }
    ): Promise<ValidationResult> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/validate/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                stepId,
                responses
            })
        });

        if (!response.ok) {
            const errorData: FormApiError = await response.json();
            throw new Error(errorData.error.message);
        }

        return await response.json();
    }

    /**
     * Submit form
     */
    static async submitForm(
        formId: string,
        submissionData: FormSubmissionRequest
    ): Promise<FormSubmissionResponse> {
        return ApiErrorHandler.withRetry(
            async () => {
                const response = await fetch(`${FORMS_BASE_URL}/${formId}/submit/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(submissionData)
                });

                if (!response.ok) {
                    const errorData: FormApiError = await response.json();
                    const submitError = new Error(errorData.error.message) as any;
                    submitError.type = errorData.error.type;
                    submitError.errors = errorData.error.errors;
                    submitError.retry_after = errorData.error.retry_after;
                    submitError.code = errorData.error.code;

                    // Don't retry validation errors or rate limit errors
                    if (
                        errorData.error.type === 'validation_error' ||
                        errorData.error.type === 'rate_limit_error' ||
                        errorData.error.type === 'recaptcha_failed' ||
                        errorData.error.type === 'authentication_required' ||
                        errorData.error.type === 'authentication_error' ||
                        errorData.error.type === 'duplicate_submission'
                    ) {
                        throw submitError;
                    }

                    submitError.status = response.status;
                    throw submitError;
                }

                return await response.json();
            },
            {
                maxRetries: 1,
                baseDelay: 1000
            },
            `submit_form_${formId}`
        );
    }

    static async getOfferCampaignStatus(offerKey: string): Promise<OfferCampaignStatus> {
        const region = this.getRegionFromPathname();
        const payload = await this.getActiveOffers(region);
        const matchingOffer = payload.data?.find(
            (offer) => offer.offerKey?.toLowerCase() === offerKey.toLowerCase()
        );

        if (!matchingOffer) {
            return {
                offerKey,
                isActive: false,
                showModal: false,
                error: 'not_found'
            };
        }

        return {
            ...matchingOffer,
            isActive: true
        };
    }

    /**
     * Save draft
     */
    static async saveDraft(
        formId: string,
        currentStep: string,
        completedSteps: string[],
        responses: { [key: string]: any },
        uploadedFiles: string[] = []
    ): Promise<{ saved: boolean; expiresAt: string }> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/session/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                currentStep,
                completedSteps,
                responses,
                uploadedFiles
            })
        });

        if (!response.ok) {
            throw new Error('Failed to save draft');
        }

        return await response.json();
    }

    /**
     * Get saved draft
     */
    static async getDraft(formId: string): Promise<FormDraft | null> {
        try {
            const response = await fetch(`${FORMS_BASE_URL}/${formId}/session/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.status === 404) {
                return null; // No draft found
            }

            if (!response.ok) {
                throw new Error('Failed to retrieve draft');
            }

            return await response.json();
        } catch (error) {
            console.error('Error retrieving draft:', error);
            return null;
        }
    }

    /**
     * Clear draft
     */
    static async clearDraft(formId: string): Promise<{ cleared: boolean }> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/session/`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error('Failed to clear draft');
        }

        return await response.json();
    }

    /**
     * Request file upload URL
     */
    static async requestFileUpload(
        formId: string,
        fileData: FileUploadRequest
    ): Promise<FileUploadResponse> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/files/upload/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(fileData)
        });

        if (!response.ok) {
            const errorData: FormApiError = await response.json();
            throw new Error(errorData.error.message);
        }

        return await response.json();
    }

    /**
     * Upload file to S3
     */
    static async uploadFileToS3(
        uploadUrl: string,
        fields: { [key: string]: string },
        file: File
    ): Promise<void> {
        let response = await this.postFileToS3(uploadUrl, fields, file);
        let responseBody = '';

        if (!response.ok) {
            responseBody = await response.text();

            const shouldRetryWithRegionalEndpoint =
                response.status === 403 &&
                uploadUrl.includes('.s3.amazonaws.com');

            if (shouldRetryWithRegionalEndpoint) {
                const regionFromError = this.getXmlValue(responseBody, 'Region');
                const regionFromCredential = this.getRegionFromCredential(fields);
                const region = regionFromError || regionFromCredential;

                if (region) {
                    const regionalUploadUrl = this.buildRegionalS3UploadUrl(uploadUrl, region);
                    if (regionalUploadUrl && regionalUploadUrl !== uploadUrl) {
                        const regionalResponse = await this.postFileToS3(regionalUploadUrl, fields, file);
                        if (regionalResponse.ok) {
                            return;
                        }
                        response = regionalResponse;
                        responseBody = await regionalResponse.text();
                    }
                }
            }
        }

        if (!response.ok) {
            const code = this.getXmlValue(responseBody, 'Code');
            const message = this.getXmlValue(responseBody, 'Message');
            const errorDetails = [code, message].filter(Boolean).join(': ');
            const reason = errorDetails || 'Unknown S3 error';
            throw new Error(`File upload to S3 failed (${response.status}) - ${reason}`);
        }
    }

    /**
     * Confirm file upload
     */
    static async confirmFileUpload(
        formId: string,
        fileId: string
    ): Promise<{ confirmed: boolean; fileId: string }> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/files/confirm/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ fileId })
        });

        if (!response.ok) {
            throw new Error('Failed to confirm file upload');
        }

        return await response.json();
    }

    /**
     * Delete uploaded file
     */
    static async deleteFile(formId: string, fileId: string): Promise<void> {
        const response = await fetch(`${FORMS_BASE_URL}/${formId}/files/${fileId}/`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (!response.ok) {
            try {
                const errorData: FormApiError = await response.json();
                throw new Error(errorData.error.message || 'Failed to delete file');
            } catch {
                throw new Error('Failed to delete file');
            }
        }
    }

    /**
     * Complete file upload flow (request + upload + confirm)
     */
    static async uploadFile(
        formId: string,
        fieldId: string,
        file: File
    ): Promise<{ fileId: string; publicUrl: string }> {
        try {
            // Step 1: Request presigned URL
            const uploadData = await this.requestFileUpload(formId, {
                fieldId,
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size
            });

            // Step 2: Upload to S3
            await this.uploadFileToS3(uploadData.uploadUrl, uploadData.fields, file);

            // Step 3: Confirm upload
            await this.confirmFileUpload(formId, uploadData.fileId);

            return {
                fileId: uploadData.fileId,
                publicUrl: uploadData.publicUrl
            };
        } catch (error) {
            console.error('File upload failed:', error);
            throw error;
        }
    }
}
